import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useGetConversationsQuery } from "../../../store/api/conversationsApi";

const RecentCommunications = () => {
	const navigate = useNavigate();
	const { data: conversations = [], isLoading } = useGetConversationsQuery();

	// Get recent conversations (last 5) - filter out any invalid IDs
	const recentCommunications = useMemo(() => {
		if (!conversations || conversations.length === 0) return [];

		return conversations
			.filter((conv: any) => conv?.id) // Only include conversations with valid IDs
			.slice(0, 5)
			.map((conv: any) => {
			// ConversationListItem has participant_count but not participant details
			// We'll use the subject and show participant count
			const participantCount = conv.participant_count || 0;
			const participantLabel =
				participantCount > 1
					? `${participantCount} participants`
					: participantCount === 1
					? "1 participant"
					: "No participants";

			// Format time
			const formatTime = (dateString: string | null) => {
				if (!dateString) return "Just now";
				const date = new Date(dateString);
				const now = new Date();
				const diffMs = now.getTime() - date.getTime();
				const diffMins = Math.floor(diffMs / 60000);
				const diffHours = Math.floor(diffMs / 3600000);
				const diffDays = Math.floor(diffMs / 86400000);

				if (diffMins < 1) return "Just now";
				if (diffMins < 60)
					return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;
				if (diffHours < 24)
					return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
				if (diffDays < 7)
					return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
				return date.toLocaleDateString();
			};

			return {
				id: conv.id,
				type: "email", // All conversations are email-based
				tenant: participantLabel,
				subject: conv.subject || "No subject",
				time: formatTime(conv.last_message_at || conv.created_at),
				status: "read", // ConversationListItem doesn't include unread count
			};
		});
	}, [conversations]);

	const handleNewMessage = () => {
		navigate("/messages");
	};

	const handleViewAll = () => {
		navigate("/messages");
	};

	const handleCommunicationClick = (conversationId: string) => {
		navigate(`/messages?conversation=${conversationId}`);
	};

	return (
		<div className="bg-card border border-border rounded-lg p-6 flex flex-col max-h-[50vh]">
			<div className="flex items-center justify-between mb-4 flex-shrink-0">
				<h3 className="text-lg font-semibold text-foreground">
					Recent Communications
				</h3>
				<Button
					variant="ghost"
					size="icon"
					onClick={handleNewMessage}
					title="New Message"
					className="h-8 w-8"
				>
					<Icon name="Plus" size={18} />
				</Button>
			</div>

			<div className="flex-1 min-h-0 overflow-y-auto space-y-3 max-h-[calc(50vh-120px)]">
				{isLoading ? (
					<div className="flex items-center justify-center py-8">
						<Icon
							name="Loader2"
							size={24}
							className="text-primary animate-spin"
						/>
					</div>
				) : recentCommunications.length === 0 ? (
					<div className="text-center py-8 text-muted-foreground">
						<Icon
							name="MessageSquare"
							size={32}
							className="mx-auto mb-2 opacity-50"
						/>
						<p className="text-sm">No recent communications</p>
					</div>
				) : (
					recentCommunications.map((comm) => (
						<div
							key={comm.id}
							onClick={() => handleCommunicationClick(comm.id)}
							className={`p-3 rounded-lg border smooth-transition hover:bg-muted/50 cursor-pointer ${
								comm.status === "unread"
									? "bg-primary/5 border-primary/20"
									: "bg-muted/20 border-border"
							}`}
						>
							<div className="flex items-start space-x-3">
								<div className="p-2 rounded-full bg-muted/50 text-primary flex-shrink-0">
									<Icon name="Mail" size={16} />
								</div>
								<div className="flex-1 min-w-0">
									<div className="flex items-center justify-between mb-1">
										<p className="text-sm font-medium text-foreground truncate">
											{comm.tenant}
										</p>
										{comm.status === "unread" && (
											<div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 ml-2"></div>
										)}
									</div>
									<p className="text-sm text-muted-foreground truncate mb-1">
										{comm.subject}
									</p>
									<p className="text-xs text-muted-foreground">{comm.time}</p>
								</div>
							</div>
						</div>
					))
				)}
			</div>

			<div className="mt-4 pt-4 border-t border-border flex-shrink-0">
				<Button
					variant="outline"
					size="sm"
					fullWidth
					iconName="MessageSquare"
					iconPosition="left"
					onClick={handleViewAll}
				>
					View
				</Button>
			</div>
		</div>
	);
};

export default RecentCommunications;
